import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import FinancialProductRulesEditGeneralDataContainer from './components/generalData/FinancialProductRulesEditGeneralDataContainer';
import FinancialProductRulesEditDetailsComponent from './components/details/FinancialProductRulesEditDetailsComponent';
import FinancialProductRuleFiltersContainer from './components/details/filters/FinancialProductRuleFiltersContainer';
import { FINANCIAL_PRODUCT_RULE_QUERY } from './queries';
import { useQuery } from 'hooks/apollo';
import { FakeText, Card } from '@totalsoft/rocket-ui';
import { GENERIC_ENTITY_BUSINESS_STATES_FLOW_QUERY, FINANCIAL_PRODUCT_STATE_QUERY } from '../../../../queries';
import { resetContent, setContent, showRightSideBar } from 'components/layout/RightSideBar';
import { RuleSidebarSummaryContainer } from './components/sidebar/summary/RuleSidebarSummaryContainer';
import { RightsContext } from 'providers/RightsContext';

const FinancialProductRulesEditContainer = ({ canWrite, ruleId, productId, onRedirect, onChangeTitle }) => {
  const { data, loading } = useQuery(FINANCIAL_PRODUCT_RULE_QUERY, {
    variables: { ruleId },
    skip: !ruleId
  });

  const { data: financialProductState } = useQuery(FINANCIAL_PRODUCT_STATE_QUERY, {
    variables: { productId },
    skip: !productId
  });

  const { data: allStates } = useQuery(GENERIC_ENTITY_BUSINESS_STATES_FLOW_QUERY, {
    variables: { flowId: financialProductState?.financialProductState?.flowId },
    skip: !financialProductState
  });

  const financialProductEditableState = useMemo(() => {
    if (financialProductState?.financialProductState?.currentStateId)
      return allStates?.genericEntityBusinessStatesXFlow?.find(
        item => item.id === financialProductState?.financialProductState?.currentStateId
      )?.editableYN;

    return false;
  }, [allStates, financialProductState?.financialProductState?.currentStateId]);

  const setSidebarContent = useCallback(() => {
    setContent(
      <RightsContext.Provider>
        <RuleSidebarSummaryContainer productId={productId} />
      </RightsContext.Provider>
    );
  }, [productId]);

  useEffect(() => {
    setSidebarContent();
    showRightSideBar(true);

    return () => {
      showRightSideBar(false);
      resetContent();
    };
  }, [setSidebarContent]);

  return loading ? (
    <Card>
      <FakeText lines={15} />
    </Card>
  ) : (
    <>
      <FinancialProductRulesEditGeneralDataContainer
        canWrite={canWrite && financialProductEditableState}
        data={data?.financialProductRule}
        ruleId={ruleId}
        productId={productId}
        onRedirect={onRedirect}
        onChangeTitle={onChangeTitle}
      />
      <FinancialProductRuleFiltersContainer
        conditions={data?.financialProductRule?.ruleFilters ?? []}
        phrase={data?.financialProductRule?.filterCondition}
        ruleId={ruleId}
        isNew={!ruleId}
        canWrite={canWrite && financialProductEditableState}
      />
      <FinancialProductRulesEditDetailsComponent
        canWrite={canWrite && financialProductEditableState}
        financialProductRule={data?.financialProductRule}
        ruleId={ruleId}
        productId={productId}
        outputDataList={data?.financialProductRule?.outputData}
        outputDictionariesList={data?.financialProductRule?.outputDictionaries}
        commissionList={data?.financialProductRule?.commissions}
        serviceList={data?.financialProductRule?.services}
        incomeList={data?.financialProductRule?.incomes}
      />
    </>
  );
};

FinancialProductRulesEditContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  ruleId: PropTypes.number,
  productId: PropTypes.number.isRequired,
  onRedirect: PropTypes.func,
  onChangeTitle: PropTypes.func
};

export default FinancialProductRulesEditContainer;
